import type { IUnitRepository, PaginatedResponse, PrincipalConflict, QueryParams } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from '@yawi/domain';
import type { Unit, UnitAllowedDevice, AddUnitAllowedDeviceResponse } from '@yawi/types';
import { UnitType } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiUnitResponse {
  id: string;
  type: 'APARTMENT' | 'HOUSE';
  name: string;
  tower?: string | null;
  floor?: string | null;
  number?: string | null;
  street?: string | null;
  exteriorNumber?: string | null;
  interiorNumber?: string | null;
  block?: string | null;
  sectionId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiCreateUnitRequest {
  type: 'APARTMENT' | 'HOUSE';
  name: string;
  tower?: string;
  floor?: string;
  number?: string;
  street?: string;
  exteriorNumber?: string;
  interiorNumber?: string;
  block?: string;
  sectionId?: string;
}

// En update los opcionales viajan como null (no undefined) para que vaciar un
// campo en el form realmente lo borre: undefined se pierde al serializar y el
// backend conservaría el valor anterior.
interface ApiUpdateUnitRequest {
  type?: 'APARTMENT' | 'HOUSE';
  name?: string;
  tower?: string | null;
  floor?: string | null;
  number?: string | null;
  street?: string | null;
  exteriorNumber?: string | null;
  interiorNumber?: string | null;
  block?: string | null;
  sectionId?: string | null;
}

interface UnitUserAssignmentResponse {
  id: string;
  userId: string;
  memberName?: string;
  unitId: string;
  isOwner: boolean;
  isPrimary: boolean;
  moveInDate?: string;
  moveOutDate?: string;
  createdAt: string;
}

// Mapea la respuesta de la API al tipo Unit interno
const mapApiUnitToUnit = (apiUnit: ApiUnitResponse): Unit => ({
  id: apiUnit.id,
  type: apiUnit.type as UnitType,
  name: apiUnit.name,
  tower: apiUnit.tower || undefined,
  floor: apiUnit.floor || undefined,
  number: apiUnit.number || undefined,
  street: apiUnit.street || undefined,
  exteriorNumber: apiUnit.exteriorNumber || undefined,
  interiorNumber: apiUnit.interiorNumber || undefined,
  block: apiUnit.block || undefined,
  sectionId: apiUnit.sectionId || undefined,
});

export class ApiUnitRepository implements IUnitRepository {
  /**
   * Obtiene TODAS las unidades paginando con el máximo permitido por el backend
   * (MAX_PAGE_SIZE = 100). Útil para operaciones masivas (p.ej. generar cuotas).
   */
  async getAllUnits(): Promise<Unit[]> {
    const all: Unit[] = [];
    let page = DEFAULT_PAGE;
    let hasNextPage = true;

    while (hasNextPage) {
      const response = await this.getUnits({
        page,
        limit: MAX_PAGE_SIZE,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      all.push(...response.data);
      hasNextPage = response.meta?.hasNextPage ?? page < (response.meta?.totalPages ?? 0);
      page += 1;
    }

    return all;
  }

  async getUnits(params?: QueryParams): Promise<PaginatedResponse<Unit>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiUnitResponse>>(
      `v1/units?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiUnitToUnit),
    };
  }

  async getPrincipalConflicts(params?: QueryParams): Promise<PaginatedResponse<PrincipalConflict>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      ...params,
    });
    return client.get<PaginatedResponse<PrincipalConflict>>(`v1/units/principal-conflicts?${queryString}`);
  }

  async createUnit(unit: Omit<Unit, 'id'>): Promise<Unit> {
    const client = getApiClient();
    const request: ApiCreateUnitRequest = {
      type: unit.type,
      name: unit.name,
      // El form limpia los campos del otro tipo de unidad a '' (y un campo
      // opcional vaciado a mano queda igual). El DTO del backend los valida con
      // @Length(1, n) y '' no es undefined, así que sin esto un alta de
      // departamento se rechaza con 400 por la calle vacía.
      tower: unit.tower || undefined,
      floor: unit.floor || undefined,
      number: unit.number || undefined,
      street: unit.street || undefined,
      exteriorNumber: unit.exteriorNumber || undefined,
      interiorNumber: unit.interiorNumber || undefined,
      block: unit.block || undefined,
      // El backend acepta sectionId (null = global); sin esto la sección
      // elegida en el formulario nunca se persistía.
      sectionId: unit.sectionId || undefined,
    };
    const apiUnit = await client.post<ApiUnitResponse>('v1/units', request);
    return mapApiUnitToUnit(apiUnit);
  }

  async updateUnit(unit: Unit): Promise<Unit> {
    const client = getApiClient();
    const request: ApiUpdateUnitRequest = {
      type: unit.type,
      name: unit.name,
      tower: unit.tower || null,
      floor: unit.floor || null,
      number: unit.number || null,
      street: unit.street || null,
      exteriorNumber: unit.exteriorNumber || null,
      interiorNumber: unit.interiorNumber || null,
      block: unit.block || null,
      // null desasigna la unidad de su sección (queda global).
      sectionId: unit.sectionId || null,
    };
    const apiUnit = await client.put<ApiUnitResponse>(`v1/units/${unit.id}`, request);
    return mapApiUnitToUnit(apiUnit);
  }

  async deleteUnit(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/units/${id}`);
  }

  async getUnitById(id: string): Promise<Unit | null> {
    const client = getApiClient();
    try {
      const apiUnit = await client.get<ApiUnitResponse>(`v1/units/${id}`);
      return mapApiUnitToUnit(apiUnit);
    } catch {
      return null;
    }
  }

  async getUnitUsers(unitId: string): Promise<UnitUserAssignmentResponse[]> {
    const client = getApiClient();
    // El backend expone `/members`, no `/users`: `/users` nunca existió y esta
    // llamada devolvía 404, así que Unidades mostraba "Sin residentes" en todas.
    // Responde UserUnitResponseDto, cuyo `memberId` es un TenantMember.id — no
    // un MasterUser.id.
    // ponytail: se remapea a `userId` en el borde para no tocar el tipo
    // compartido ni el mock; renombrar el campo es su propio cambio.
    const members = await client.get<
      Array<Omit<UnitUserAssignmentResponse, 'userId'> & { memberId: string }>
    >(`v1/units/${unitId}/members`);
    return members.map(({ memberId, ...rest }) => ({ ...rest, userId: memberId }));
  }

  async assignUser(userId: string, unitId: string, options?: { isOwner?: boolean; isPrimary?: boolean; moveInDate?: string }): Promise<{ id: string; userId: string; unitId: string; isOwner: boolean; isPrimary: boolean; moveInDate?: string; moveOutDate?: string; createdAt: string }> {
    const client = getApiClient();
    return await client.post('v1/units/assignments', {
      masterUserId: userId,
      unitId,
      isOwner: options?.isOwner || false,
      isPrimary: options?.isPrimary ?? true,
      moveInDate: options?.moveInDate,
    });
  }

  async updateAssignment(assignmentId: string, updates: { isOwner?: boolean; isPrimary?: boolean; moveInDate?: string; moveOutDate?: string }): Promise<{ id: string; userId: string; unitId: string; isOwner: boolean; isPrimary: boolean; moveInDate?: string; moveOutDate?: string; createdAt: string }> {
    const client = getApiClient();
    return await client.put(`v1/units/assignments/${assignmentId}`, updates);
  }

  async unassignUser(assignmentId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/units/assignments/${assignmentId}`);
  }

  async assignUnitsToSection(
    unitIds: string[],
    sectionId: string | null
  ): Promise<{ updated: number }> {
    const client = getApiClient();
    return await client.patch<{ updated: number }>('v1/units/sections', { unitIds, sectionId });
  }

  async getAllowedDevices(unitId: string): Promise<UnitAllowedDevice[]> {
    const client = getApiClient();
    return await client.get<UnitAllowedDevice[]>(`v1/units/${unitId}/allowed-devices`);
  }

  async addAllowedDevice(unitId: string, deviceId: string): Promise<AddUnitAllowedDeviceResponse> {
    const client = getApiClient();
    return await client.post<AddUnitAllowedDeviceResponse>(
      `v1/units/${unitId}/allowed-devices`,
      { deviceId },
    );
  }

  async removeAllowedDevice(unitId: string, deviceId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/units/${unitId}/allowed-devices/${deviceId}`);
  }
}

